import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { object, picklist } from "valibot";
import { dataName, label, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { NUMBER_RANGE_CONDITION_KEYS } from "../../constants";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { PreviewContentLabel, SmileyPreview } from "../FieldPreviewComponents";
import { keyFromSelector } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  required: required(),
});
const ValueSchema = picklist([1, 2, 3, 4, 5]);

export type SmileyFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const SmileyField: Field<SmileyFieldProperties, DefaultDtoType, ValueType> = {
  type: "smiley",
  category: "choices",
  widget: "com.moreapps.plugin:smiley:4",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({ required: false }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_RANGE_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: ["calculation", "lookup", "number", "radio", "rating", "search", "slider", "smiley"],
        variableTypes: ["number"],
      },
    },
  },
  filter: {
    type: "number",
    definition: [
      {
        operator: "is",
        inputType: "select",
        placeholderLabel: keyFromSelector(($) => $.TABLE_FILTER_SMILEY_IS_PLACEHOLDER),
        options: [
          { id: "1", label: keyFromSelector(($) => $.SMILEY_FIELD_VALUE_1_LABEL) },
          { id: "2", label: keyFromSelector(($) => $.SMILEY_FIELD_VALUE_2_LABEL) },
          { id: "3", label: keyFromSelector(($) => $.SMILEY_FIELD_VALUE_3_LABEL) },
          { id: "4", label: keyFromSelector(($) => $.SMILEY_FIELD_VALUE_4_LABEL) },
          { id: "5", label: keyFromSelector(($) => $.SMILEY_FIELD_VALUE_5_LABEL) },
        ],
      },
      isFilledInFilter,
    ],
  },
  stringValue: (value) => value.toString(),
  renderValue: (value) => {
    const { t } = useTranslation();
    switch (value) {
      case 1:
        return t(($) => $.SMILEY_FIELD_VALUE_1_LABEL);
      case 2:
        return t(($) => $.SMILEY_FIELD_VALUE_2_LABEL);
      case 3:
        return t(($) => $.SMILEY_FIELD_VALUE_3_LABEL);
      case 4:
        return t(($) => $.SMILEY_FIELD_VALUE_4_LABEL);
      case 5:
        return t(($) => $.SMILEY_FIELD_VALUE_5_LABEL);
    }
  },
  icon: "emojiHappy",
  titleKey: keyFromSelector(($) => $.SMILEY_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.SMILEY_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <SmileyPreview />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(SmileyField);
